import { parse, isValid, addDays, addMonths, addYears } from 'date-fns';
import { defaultDateFormat, today, formatDateFromJSDate } from '../../utilities';

function expandDate(value: string): string {
  const digitPattern = /^\d*$/;
  const isAllDigits = digitPattern.test(value);

  if (isAllDigits) {
    switch (value.length) {
      // ddmm
      case 4:
        return `${value.substring(0, 2)}-${value.substring(2, 4)}-${new Date().getFullYear()}`;
      // ddmmyy
      case 6: {
        const year = parseInt(value.substring(4, 6), 10);
        const adjustedYear = year + (year < 30 ? 2000 : 1900);
        return `${value.substring(0, 2)}-${value.substring(2, 4)}-${adjustedYear}`;
      }
      // ddmmyyyy
      case 8:
        return `${value.substring(0, 2)}-${value.substring(2, 4)}-${value.substring(4, 8)}`;
      default:
        break;
    }
  }
  return value;
}

export function validateRelativeDateFormat(value: string): Date | undefined {
  try {
    const isStringValue = typeof value === 'string';

    if (!isStringValue) {
      return undefined;
    }

    // Pattern: y/m/d followed by optional +/- and digits, can repeat
    // Matches patterns like: d5, m3, m3d4, y-2m3, etc.
    const pattern = /^([ymd][+-]?\d+)+$/i;
    const isValidPattern = pattern.test(value);

    if (!isValidPattern) {
      return undefined;
    }

    // Parse pattern: extract all [unit][value] pairs
    const matchPattern = /([ymd])([+-]?\d+)/gi;
    const matches: RegExpExecArray[] = [];
    let matchResult = matchPattern.exec(value);

    while (matchResult) {
      matches.push(matchResult);
      matchResult = matchPattern.exec(value);
    }

    const matchCount = matches.length;

    if (matchCount === 0) {
      return undefined;
    }

    let result = today();

    for (let index = 0; index < matchCount; index += 1) {
      const match = matches[index];

      if (!match) {
        return undefined;
      }

      const unitRaw = match[1];
      const amountRaw = match[2];
      const unit = unitRaw.toLowerCase();
      const amount = parseInt(amountRaw, 10);

      if (Number.isNaN(amount)) {
        return undefined;
      }

      switch (unit) {
        case 'd':
          result = addDays(result, amount);
          break;
        case 'm':
          result = addMonths(result, amount);
          break;
        case 'y':
          result = addYears(result, amount);
          break;
        default:
          return undefined;
      }
    }

    return result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('validateRelativeDateFormat failed', { value, error });
    return undefined;
  }
}

export function validateDefaultDateFormat(value: string): Date | undefined {
  const date = parse(value, defaultDateFormat, today());
  if (isValid(date)) {
    const formattetDate = formatDateFromJSDate(date);
    if (formattetDate !== '01-01-0001' && formattetDate !== '31-12-9999') {
      return date;
    }
  }
  return undefined;
}

export function validateExpandedDateFormat(value: string): Date | undefined {
  const expanded = expandDate(value);
  if (expanded !== value) {
    return validateDefaultDateFormat(expanded);
  }
  return undefined;
}
